import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
/* eslint-disable */

/** One entry in the skin's reserved `assets` map. */
export interface SkinAsset {
  type: string; // S3 prefix / category: "logo" | "favicon" | "font"
  filename: string; // e.g. "main.png"
  url?: string | null; // presigned URL, present on GET (null if missing in S3)
}

/**
 * The skin document as stored by NETSYNCIO-API (the "entire skin JSON").
 *
 * POST /skin stores this verbatim (validating the reserved `assets` map) and
 * GET /skin returns it back with each asset enriched with a presigned `url`.
 * The identity key is `skin_name`. The colors are the corporate look; `font`
 * and `typeScale` are per-user overrides the backend merges in on GET /skin/user.
 * `logoMark` / `scope` are extra frontend metadata the passthrough store keeps.
 */
export interface SkinDto {
  skin_name?: string;
  appTitle?: string;
  radius?: number;
  logoMark?: string;
  scope?: string;
  colors?: {
    primary?: string;
    secondary?: string;
    link?: string;
    sidenav?: string;
    splash?: string;
    background?: string;
    surface?: string;
    text?: string;
    darkBackground?: string;
    darkSurface?: string;
    darkText?: string;
  };
  // --- per-user overrides ---
  font?: string;
  typeScale?: number;
  // --- reserved assets map ---
  assets?: Record<string, SkinAsset>;
}

/** The per-user override document (POST /skin/user). */
export interface UserOverridesDto {
  font?: string;
  typeScale?: number;
}

/** One row of GET /skin/list — the raw skin JSON plus timestamps (assets NOT enriched). */
export interface SkinListEntry {
  skin_name: string;
  created_at?: string;
  updated_at?: string;
  skin: SkinDto;
}

export interface SkinListResponse {
  skins: SkinListEntry[];
}

/** `custom` skins live in the backend; `predefined` skins live on the frontend. */
export type SkinType = 'custom' | 'predefined';

/**
 * GET /skin/active. When custom, the full enriched `skin` is included; when
 * predefined, only the pointer (`skin_name`) is returned — the frontend
 * resolves the actual look from its presets. 404 when no active skin is set.
 */
export interface ActiveSkinResponse {
  skin_type: SkinType;
  skin_name: string;
  skin?: SkinDto;
}

/**
 * GET /skin/user — the active skin merged with the caller's overrides.
 *  - custom     : backend merges & returns the finished `skin` (assets enriched).
 *  - predefined : backend has no copy, so it returns the raw `overrides` plus the
 *                 pointer name; the frontend merges locally against its preset.
 * 404 when no active skin is set.
 */
export interface MergedSkinResponse {
  skin_type: SkinType;
  skin_name: string;
  skin?: SkinDto;
  overrides?: UserOverridesDto;
}

export interface SetActiveRequest {
  skin_type: SkinType;
  skin_name: string;
}

export interface PresignResponse {
  upload_url: string;
  asset_type: string;
  filename: string;
  warning?: string;
}

export interface AssetListResponse {
  assets: Record<
    string,
    Array<{ filename: string; size: number; last_modified: string; url: string }>
  >;
}

/**
 * Client for the NETSYNCIO Customer Skinning API.
 *
 * URLs are relative — InterceptService prepends `environment.apiBaseUrl` and
 * attaches the Bearer token. The S3 upload is the one exception: it must NOT
 * carry our Authorization header, so it goes out via `fetch`, bypassing the
 * Angular interceptor entirely.
 */
@Injectable({ providedIn: 'root' })
export class BrandApiService {
  constructor(private http: HttpClient) {}

  // ---------- skins ----------

  /** The active skin merged with the calling user's overrides. 404 if none. */
  getMergedSkin(): Observable<MergedSkinResponse> {
    return this.http.get<MergedSkinResponse>('skin/user');
  }

  /** Fetch a single named skin, assets enriched with presigned URLs. */
  getCustomerSkin(skinName: string): Observable<SkinDto> {
    const params = new HttpParams().set('skin_name', skinName);
    return this.http.get<SkinDto>('skin', { params });
  }

  /** List the customer's custom skins (predefined skins live on the frontend). */
  listSkins(): Observable<SkinListResponse> {
    return this.http.get<SkinListResponse>('skin/list');
  }

  /** Fully rewrite a skin. The body is the entire skin JSON (keyed by skin_name). */
  saveCustomerSkin(skin: SkinDto): Observable<{ message: string; skin_name: string }> {
    return this.http.post<{ message: string; skin_name: string }>('skin', skin);
  }

  /** Delete a custom skin. Clears the active pointer if it referenced this skin. */
  deleteSkin(skinName: string): Observable<{ message: string }> {
    const params = new HttpParams().set('skin_name', skinName);
    return this.http.delete<{ message: string }>('skin', { params });
  }

  /** Set the active skin pointer for the customer. */
  setActiveSkin(skinType: SkinType, skinName: string): Observable<{ message: string }> {
    const body: SetActiveRequest = { skin_type: skinType, skin_name: skinName };
    return this.http.post<{ message: string }>('skin/active', body);
  }

  /** Get the active skin pointer (+ full skin when custom). 404 if none set. */
  getActiveSkin(): Observable<ActiveSkinResponse> {
    return this.http.get<ActiveSkinResponse>('skin/active');
  }

  /** Save the calling user's own overrides (font, type scale). */
  saveUserOverrides(overrides: UserOverridesDto): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('skin/user', overrides);
  }

  // ---------- assets ----------

  /** Ask for a presigned S3 URL to upload an asset. */
  requestAssetUpload(assetType: string, filename: string): Observable<PresignResponse> {
    return this.http.post<PresignResponse>('skin/asset', {
      asset_type: assetType,
      filename,
    });
  }

  /** List uploaded assets, optionally filtered to one type. */
  listAssets(assetType?: string): Observable<AssetListResponse> {
    let params = new HttpParams();
    if (assetType) params = params.set('asset_type', assetType);
    return this.http.get<AssetListResponse>('skin/asset', { params });
  }

  /** Delete an asset file. Does not touch the skin's assets map. */
  deleteAsset(assetType: string, filename: string): Observable<{ message: string }> {
    const params = new HttpParams()
      .set('asset_type', assetType)
      .set('filename', filename);
    return this.http.delete<{ message: string }>('skin/asset', { params });
  }

  /**
   * PUT the file straight to S3 using the presigned URL.
   * Deliberately uses fetch: the Angular interceptor would inject our Bearer
   * token, which S3 rejects on a presigned request.
   */
  async uploadToPresignedUrl(uploadUrl: string, file: File): Promise<void> {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    if (!res.ok) {
      throw new Error(`Asset upload failed (${res.status})`);
    }
  }

  /** Presign + upload in one step. Returns the asset entry to store in the skin. */
  async uploadAsset(assetType: string, file: File): Promise<SkinAsset> {
    const presign = await new Promise<PresignResponse>((resolve, reject) =>
      this.requestAssetUpload(assetType, file.name).subscribe({
        next: resolve,
        error: reject,
      }),
    );
    await this.uploadToPresignedUrl(presign.upload_url, file);
    return { type: assetType, filename: presign.filename };
  }
}
